// C:\Projects\texe\texe-web\src\pages\UserProfile.tsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL, apiGet } from "../lib/api";
import FollowButton from "../components/FollowButton";
import { useAuth } from "../context/AuthProvider";
import Linkify from "linkify-react";
import MoreMenuForUser from "../components/MoreMenuForUser";
import { type PostItem } from "../components/PostCard";
import Avatar from "../components/Avatar";
import ImageModal from "../components/ImageModal";
import PageWithAds from "../components/PageWithAds";
import { useFeedMeta } from "../hooks/useFeedMeta";
import { useGlobalPostInteractions } from "../context/PostInteractionsProvider";
import FollowersModal from "../components/FollowersModal";
import RankTooltip from "../components/RankTooltip";
import RichText from "../components/RichText";

type PublicUser = {
  id: string;
  username: string;
  email?: string;
  createdAt?: string;
  counts: { followers: number; following: number };
  bio?: string | null;
  favoriteQuote?: string | null;
  website?: string | null;
  avatarUrl?: string | null;
  avatarUrlSmall?: string | null;

  // ⬇️ GOLD için (backend gönderiyorsa doğrudan kullanırız)
  roles?: string[];
  isAdmin?: boolean;
  highlight?: "gold" | null;
  totalStars?: number;
  
  // ⬇️ SavWar rütbe bilgileri
  savwarTP?: number;
  savwarRank?: number;

  // ⬇️ Premium fields
  activeFrame?: string | null;
  activeBadge?: string | null;
  activeBackground?: string | null;
};

function normalizeUrl(u?: string | null) {
  if (!u) return null;
  const s = u.trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
}

function absImg(url?: string | null): string | undefined {
  if (!url) return undefined;
  return url.startsWith("/") ? `${API_BASE_URL}${url}` : url;
}

// PostCard ile aynı mantık: admin/mod veya highlight=gold → true
function isGoldUser(u?: Pick<PublicUser, "roles" | "isAdmin" | "highlight"> | null, fallbackItems?: PostItem[]) {
  if (!u) {
    // Kullanıcı nesnesi gold bilgisi vermiyorsa, postlardan infer et (opsiyonel fallback)
    if (fallbackItems && fallbackItems.length > 0) {
      return fallbackItems.some((p) => {
        if (p.highlight === "gold") return true;
        const a = p.author;
        if (!a) return false;
        if ((a as any).isAdmin) return true;
        const roles = (a as any).roles as string[] | undefined;
        return Array.isArray(roles) && (roles.includes("admin") || roles.includes("moderator"));
      });
    }
    return false;
  }
  if (u.highlight === "gold") return true;
  if (u.isAdmin) return true;
  return Array.isArray(u.roles) && (u.roles.includes("admin") || u.roles.includes("moderator"));
}

function UserProfile() {
  const { username = "", userId = "" } = useParams();
  const { user: me } = useAuth();
  const navigate = useNavigate();

  const [u, setU] = useState<PublicUser | null>(null);
  const [items, setItems] = useState<PostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [openAvatar, setOpenAvatar] = useState(false);
  const [followersModal, setFollowersModal] = useState<"followers" | "following" | null>(null);

  // Post interactions hook
  const { getInteraction, toggleLike, batchLoadInteractions } = useGlobalPostInteractions();

  const isMe = useMemo(() => {
    if (!me || !u) return false;
    return me.id === u.id || me.username === u.username;
  }, [me, u]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      setError(null);
      setBlocked(false);
      try {
        // userId veya username ile kullanıcı bilgisi çek
        const identifier = userId || username;
        if (!identifier) {
          setError("Kullanıcı bilgisi eksik.");
          setLoading(false);
          return;
        }

        // kullanıcı bilgisi
        const res = await apiGet<{ ok: true; user: PublicUser }>(
          `/api/users/${encodeURIComponent(identifier)}`
        );
        if (ignore) return;
        setU(res.user);

        // kullanıcının gönderileri
        const posts = await apiGet<{ ok: true; items: PostItem[] }>(
          `/api/posts/by/${encodeURIComponent(res.user.id)}`
        );
        if (ignore) return;
        setItems(posts.items);

        // Like ve yorum sayılarını yükle
        if (posts.items.length > 0) {
          await batchLoadInteractions(posts.items.map(p => p.id));
        }
      } catch (e: any) {
        if (ignore) return;
        const msg = String(e?.message || "");
        if (msg === "blocked") setBlocked(true);
        else if (msg === "user_not_found" || msg === "not_found")
          setError("Kullanıcı bulunamadı.");
        else setError("Bir şeyler ters gitti. Lütfen tekrar deneyin.");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [username, userId, batchLoadInteractions]);

  // (opsiyonel) batch meta preload
  const postIds = useMemo(() => items.map((p) => p.id), [items]);
  const authorIds = useMemo(() => (u?.id ? [u.id] : []), [u?.id]);
  useFeedMeta({ postIds, authorIds, enabled: items.length > 0 || !!u?.id });

  if (loading) return <div style={{ padding: 24 }}>Yükleniyor...</div>;
  if (blocked)
    return (
      <div style={{ padding: 24 }}>
        Bu profili görüntüleyemezsiniz: Kullanıcı ile aranızda engel bulunuyor.
      </div>
    );
  if (error) return <div style={{ padding: 24 }}>{error}</div>;
  if (!u) return <div style={{ padding: 24 }}>Kullanıcı bulunamadı.</div>;

  const avatarSmall = absImg(u.avatarUrlSmall || u.avatarUrl);
  const avatarBig = absImg(u.avatarUrl || u.avatarUrlSmall);

  // ⬇️ GOLD hesapla (user alanlarından ya da postlardan fallback)
  const isGold = isGoldUser(u, items);

  // Premium background style
  const headerStyle = u.activeBackground
    ? {
        backgroundImage: `url(${u.activeBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: "0 auto" }}>
      {/* Premium Background Header */}
      {u.activeBackground && (
        <div
          style={{
            width: "100%",
            height: 160,
            borderRadius: 12,
            marginBottom: 16,
            position: "relative",
            overflow: "hidden",
            ...headerStyle,
          }}
        >
          {/* Gradient overlay for better text visibility */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)",
            }}
          />
        </div>
      )}

      {/* Avatar - ortada, büyük + Premium Frame */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
        <div
          onClick={() => avatarBig && setOpenAvatar(true)}
          style={{
            cursor: avatarBig ? "zoom-in" : "default",
            position: "relative",
          }}
          title={avatarBig ? "Büyüt" : undefined}
        >
          <div
            style={{
              borderRadius: "50%",
              padding: 3,
              border: isGold ? "3px solid gold" : "3px solid #114232",
              boxShadow: isGold ? "0 0 20px rgba(255,215,0,0.3)" : "none",
            }}
          >
            <Avatar src={avatarSmall} alt={u.username} size={120} />
          </div>
          {/* Premium Frame Overlay */}
          {u.activeFrame && (
            <img
              src={u.activeFrame}
              alt="Premium Frame"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                objectFit: "contain",
              }}
            />
          )}
        </div>
      </div>

      {/* Kullanıcı adı ve SavLike yıldızı */}
      <div style={{ textAlign: "center", marginBottom: 20, overflow: "visible" }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", 
          gap: 12, 
          flexWrap: "wrap",
          marginBottom: 12,
          overflow: "visible",
        }}>
          {/* SavWar Rütbe İkonu - Daha büyük */}
          {u.savwarRank && u.savwarRank > 0 && (
            <div style={{ 
              fontSize: 40,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              overflow: "visible",
            }}>
              <RankTooltip rank={u.savwarRank} tp={u.savwarTP || 0} username={u.username} />
            </div>
          )}
        </div>
        
        <h2 style={{ margin: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ color: isGold ? "gold" : "#fff", fontSize: 28, fontWeight: 700 }}>
            {u.username}
          </span>
          {/* Premium Badge */}
          {u.activeBadge && (
            <img
              src={u.activeBadge}
              alt="Premium Badge"
              style={{
                width: 24,
                height: 24,
                objectFit: "contain",
              }}
            />
          )}
          {u.totalStars && u.totalStars > 0 && (
            <span style={{ color: "#FFD700", fontSize: 20 }}>⭐{u.totalStars.toFixed(1)}</span>
          )}
          {isGold && (
            <span
              title="Admin/Moderatör"
              style={{
                fontSize: 11,
                padding: "3px 10px",
                borderRadius: 999,
                border: "1px solid rgba(255,215,0,0.7)",
                background: "rgba(255,215,0,0.12)",
                color: "gold",
                letterSpacing: 0.3,
                fontWeight: 600,
              }}
            >
              GOLD
            </span>
          )}
        </h2>
        {u.email && (
          <div style={{ color: "#888", fontSize: 14, marginTop: 8 }}>{u.email}</div>
        )}
      </div>

      {/* Stats - mobile gibi card tarzı */}
      <div style={{ 
        display: "flex", 
        gap: 12, 
        marginBottom: 24,
        justifyContent: "space-around",
      }}>
        <div style={{
          flex: 1,
          background: "#1a1a1a",
          border: "2px solid #114232",
          borderRadius: 12,
          padding: "16px 12px",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#114232", marginBottom: 4 }}>
            {items.length}
          </div>
          <div style={{ fontSize: 13, color: "#888" }}>Gönderiler</div>
        </div>
        <div 
          onClick={() => {
            if (!me) {
              navigate('/login');
              return;
            }
            setFollowersModal("followers");
          }}
          style={{
            flex: 1,
            background: "#1a1a1a",
            border: "2px solid #114232",
            borderRadius: 12,
            padding: "16px 12px",
            textAlign: "center",
            cursor: "pointer",
            transition: "transform 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.02)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 700, color: "#114232", marginBottom: 4 }}>
            {u.counts.followers}
          </div>
          <div style={{ fontSize: 13, color: "#888" }}>Takipçi</div>
        </div>
        <div 
          onClick={() => {
            if (!me) {
              navigate('/login');
              return;
            }
            setFollowersModal("following");
          }}
          style={{
            flex: 1,
            background: "#1a1a1a",
            border: "2px solid #114232",
            borderRadius: 12,
            padding: "16px 12px",
            textAlign: "center",
            cursor: "pointer",
            transition: "transform 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.02)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 700, color: "#114232", marginBottom: 4 }}>
            {u.counts.following}
          </div>
          <div style={{ fontSize: 13, color: "#888" }}>Takip</div>
        </div>
      </div>

      {/* Profili Düzenle butonu (kendi profilinde) */}
      {isMe && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <button
            onClick={() => window.location.href = "/settings"}
            style={{
              padding: "12px 32px",
              borderRadius: 8,
              border: "2px solid #114232",
              background: "#114232",
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            ✏️ Profili Düzenle
          </button>
        </div>
      )}

      {/* Takip butonu ve menü (başkasının profilinde) */}
      {!isMe && (
        <div style={{ display: "flex", gap: 12, marginBottom: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <FollowButton userId={u.id} username={u.username} />
          <button
            onClick={() => {
              const url = `${window.location.origin}/u/${u.username}`;
              navigator.clipboard.writeText(url).then(() => {
                alert("🔗 Profil linki kopyalandı!");
              }).catch(() => {
                alert("Link kopyalanamadı");
              });
            }}
            style={{
              padding: "10px 24px",
              borderRadius: 8,
              border: "2px solid #114232",
              background: "#114232",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            🔗 Profili Paylaş
          </button>
          <MoreMenuForUser targetUserId={u.id} />
        </div>
      )}

      <div style={{ opacity: 0.75, fontSize: 12, marginBottom: 20, textAlign: "center" }}>
        Katıldı: {u.createdAt ? new Date(u.createdAt).toLocaleDateString("tr-TR") : "-"}
      </div>

      {/* Bio card */}
      {u.bio && (
        <div
          style={{
            border: "2px solid #114232",
            borderRadius: 12,
            padding: 16,
            marginBottom: 12,
            background: "#1a1a1a",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: "#114232", marginBottom: 8 }}>Hakkında</div>
          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 14, lineHeight: 1.5 }}>
            <Linkify options={{ target: "_blank", rel: "noopener noreferrer", className: "post-link" }}>
              {u.bio}
            </Linkify>
          </div>
        </div>
      )}

      {/* Quote card */}
      {u.favoriteQuote && (
        <div
          style={{
            border: "2px solid #114232",
            borderRadius: 12,
            padding: 16,
            marginBottom: 12,
            background: "#1a1a1a",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: "#114232", marginBottom: 8 }}>Favori Söz</div>
          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 14, lineHeight: 1.5, fontStyle: "italic" }}>
            <Linkify options={{ target: "_blank", rel: "noopener noreferrer", className: "post-link" }}>
              {`"${u.favoriteQuote}"`}
            </Linkify>
          </div>
        </div>
      )}

      {/* Website card */}
      {u.website && (
        <div
          style={{
            border: "2px solid #114232",
            borderRadius: 12,
            padding: 16,
            marginBottom: 12,
            background: "#1a1a1a",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: "#114232", marginBottom: 8 }}>Website</div>
          <div style={{ fontSize: 14 }}>
            {normalizeUrl(u.website) ? (
              <a 
                href={normalizeUrl(u.website)!} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: "#4A90E2", textDecoration: "underline" }}
              >
                {u.website}
              </a>
            ) : (
              <span>{u.website}</span>
            )}
          </div>
        </div>
      )}

      {/* Gönderiler Bölümü */}
      <div style={{ marginTop: 24 }}>
        <div style={{ 
          fontSize: 20, 
          fontWeight: 800, 
          marginBottom: 16,
          color: "#114232"
        }}>
          Gönderiler ({items.length})
        </div>

        {items.length === 0 ? (
          <div style={{
            color: "#666",
            fontSize: 14,
            textAlign: "center",
            padding: "24px 0"
          }}>
            Henüz gönderi yok
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {items.map((p) => {
              const interaction = getInteraction(p.id);
              return (
                <div
                  key={p.id}
                  onClick={() => navigate(`/post/${p.id}`)}
                  style={{
                    backgroundColor: "#1a1a1a",
                    border: "2px solid #114232",
                    borderRadius: 12,
                    padding: 16,
                    cursor: "pointer",
                    transition: "opacity 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.95")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  {/* Tarih */}
                  <div style={{
                    color: "#666",
                    fontSize: 12,
                    marginBottom: 8
                  }}>
                    {new Date(p.createdAt).toLocaleDateString("tr-TR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    })}
                  </div>

                  {/* Gönderi Metni */}
                  <div style={{
                    color: "#fff",
                    fontSize: 15,
                    lineHeight: "22px",
                    marginBottom: 12
                  }}>
                    <RichText text={p.text} />
                  </div>

                  {/* İstatistikler */}
                  <div style={{
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    marginTop: 12
                  }}>
                    {/* Like Butonu */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLike(p.id);
                      }}
                      disabled={interaction.loading}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "8px 12px",
                        borderRadius: 20,
                        backgroundColor: interaction.liked 
                          ? "rgba(255, 0, 0, 0.1)" 
                          : "rgba(255,255,255,0.05)",
                        border: interaction.liked
                          ? "1px solid rgba(255, 0, 0, 0.3)"
                          : "1px solid rgba(255,255,255,0.1)",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                    >
                      <span style={{ fontSize: 16 }}>
                        {interaction.liked ? "❤️" : "🤍"}
                      </span>
                      <span style={{
                        color: "#ccc",
                        fontSize: 14,
                        fontWeight: 600
                      }}>
                        {interaction.likeCount}
                      </span>
                    </button>

                    {/* Yorum Sayısı */}
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "8px 12px",
                      borderRadius: 20,
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)"
                    }}>
                      <span style={{ fontSize: 16 }}>💬</span>
                      <span style={{
                        color: "#ccc",
                        fontSize: 14,
                        fontWeight: 600
                      }}>
                        {interaction.commentCount ?? 0}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ImageModal
        open={openAvatar}
        src={avatarBig}
        alt={u.username}
        caption={`@${u.username}`}
        onClose={() => setOpenAvatar(false)}
      />

      {followersModal && (
        <FollowersModal
          userId={u.id}
          type={followersModal}
          onClose={() => setFollowersModal(null)}
        />
      )}
    </div>
  );
}

// Wrap with ads
export default function UserProfileWithAds() {
  return (
    <PageWithAds leftAdSlot="1740099870" rightAdSlot="3468114516">
      <UserProfile />
    </PageWithAds>
  );
}
